import { ChangeDetectionStrategy, Component, effect, Input, input, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { View } from '../base.component';
import { CustomizationPickerComponent, CustomizationPickerItem } from '../customization-picker/customization-picker.component';
import { GenericModalComponent } from '../ui/generic-modal/generic-modal.component';
import { PetColorConfig, PetDTO, PetSpeciesConfig, UserUpload } from 'src/app/dto';
import { AuthService } from 'src/app/services/auth.service';
import { CustomizationService } from 'src/app/services/customization/customization.service';
import { FeatureFlagService } from 'src/app/services/feature-flag.service';
import { PetService } from 'src/app/services/pet.service';
import { UIStateService } from 'src/app/services/ui-state.service';
import { UserService } from 'src/app/services/user.service';
import { retry, retryWhen, Subscription, tap, throwError } from 'rxjs';

@Component({
    selector: 'app-profile-pet',
    templateUrl: './profile-pet.component.html',
    styleUrls: ['./profile-pet.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfilePetComponent extends View implements OnInit {
    public static route = 'pet';

    @Input() petIdData;
    @Input() ownedByViewer = false;

    // Aliased to the route param name ('petId') while keeping a distinct property name, since
    // petId below is a plain mutable field fed by three different sources (this route param, or
    // either of the two @Input()s above when embedded as a child rather than routed directly).
    petIdParam = input<string>(undefined, { alias: 'petId' });

    private petId: number;
    // Backed by a signal, not a plain field - getPet()'s subscribe below reassigns this
    // internally (routedPetId can change and re-fetch under this app's route reuse strategy),
    // which is invisible to OnPush as a plain field write. The getter keeps every pet.X read in
    // the template working unchanged; no external caller ever binds [pet] directly (grepped),
    // so the @Input() here is effectively self-managed already.
    private petState = signal<PetDTO>(undefined);
    @Input() set pet(value: PetDTO) { this.petState.set(value); }
    get pet(): PetDTO { return this.petState(); }
    // Set inside getPet()'s subscribe error handler (async) as well as synchronously in the
    // effect/ngOnInit below - needs to be a signal for the async write to be visible.
    protected errorState = signal(false);
    private getPetSub: Subscription;

    constructor(private petService: PetService, authService: AuthService, uiStateService: UIStateService,
        private customizationService: CustomizationService, public featureFlagService: FeatureFlagService) {
        super(authService, uiStateService);
        effect(() => {
            const routedPetId = this.petIdParam();
            // Only the direct-route case (neither @Input() populated) is driven by this input -
            // the other two branches are handled synchronously in ngOnInit below, same as before.
            if (routedPetId && !this.petIdData && !this.pet) {
                this.errorState.set(false);
                this.petId = Number(routedPetId);
                this.getPet();
            }
        });
    }

    ngOnInit(): void {
        this.errorState.set(false);

        if (this.petIdData && !this.pet) {
            this.petId = this.petIdData;
            this.getPet();
        } else if (this.pet) {
            this.petId = this.pet.Id;
        }
    }

    private getPet() {
        // Cancel any still-pending fetch before starting a new one: routedPetId can change and
        // re-trigger this from the constructor's effect() (navigating between two directly-routed
        // pets while reusing this component instance under this app's route reuse strategy) before
        // an earlier fetch resolves, which would otherwise let a stale response win the race and
        // overwrite this.pet with the wrong pet's data.
        this.getPetSub?.unsubscribe();
        this.getPetSub = this.petService.getPets([this.petId])
            .pipe(
                tap(pets => {
                    if (!pets || pets.length != 1)
                        throwError(() => "Failed to get pet!");
                }),
                retry({ count: 10, delay: 1200 })
            )
            .subscribe({
                next: pets => { this.pet = pets[0]; },
                error: () => this.errorState.set(true)
            });
        this.subscriptions.push(this.getPetSub);
    }

    getImagePath(psc: PetSpeciesConfig, pcc: PetColorConfig): string {
        return PetDTO.GetImagePath(psc, pcc);
    }

    openBackgroundPicker(): void {
        if (!this.ownedByViewer || !this.pet) { return; }
        this.customizationService.initializeOwnedPetBackgrounds(this.pet.Id);
        this.uiStateService.openModalWithComponent(
            { size: 'lg' }, null, 'Edit Pet Background', CustomizationPickerComponent, GenericModalComponent,
            (picker: CustomizationPickerComponent) => {
                picker.equippedId.set(this.pet.Background?.Id ?? null);
                picker.defaultLabel = 'No Background';
                this.subscribe(this.customizationService.ownedPetBackgroundsFor(this.pet.Id), owned => picker.items.set(owned as unknown as CustomizationPickerItem[]));
                picker.onSelect = item => this.customizationService.equipPetBackground(this.pet.Id, item as any);
            });
    }

}
